import {
  ADDON_KEYS,
  type AddonKey,
  type AddonSelection,
  type PlanPricing,
  planHue,
} from '../../shared/plan';
import type { PlanOption } from '../../ui/v2';
import { type Plan, formatDate, pricingFromPlan } from '../Billing/model';

/**
 * Activation V2 — shapes for `GET /api/public/activation/offers/{token}/`
 * and `GET /api/public/activation/plans/?country=&store_uuid=`. The page
 * shows either the standard catalog (no token) or an offer, which comes in
 * three shapes:
 *
 * - single: one custom-priced plan for one business;
 * - multi (`is_group` + `offers[]`): a menu of custom offers, pick one;
 * - group-scoped (`is_group_scoped` + `locations[]`): one plan across a
 *   frozen roster of existing stores, each with its own add-on switches.
 *
 * Any of them may be fee only (`checkout_mode === 'activation_fee_only'`):
 * the signer pays the setup fee today; the plan starts on
 * `billing_start_date`.
 */
export type Country = 'US' | 'CA';
export const COUNTRIES: Country[] = ['US', 'CA'];
export const COUNTRY_LABEL: Record<string, string> = {
  US: 'United States · USD',
  CA: 'Canada · CAD',
};
export const COUNTRY_CURRENCY: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
};

export interface GroupLocation {
  store_id: number;
  store_name: string;
  multilingual_addon_active: boolean;
  sms_ordering_addon_active: boolean;
  opentable_resy_addon_active: boolean;
}

export interface Offer {
  token: string;
  status: string;
  business_name: string;
  signer_name?: string;
  signer_email?: string;
  signer_phone?: string;
  currency: string;
  setup_fee_cents: number;
  base_price_cents: number;
  included_minutes: number;
  overage_rate_cents_per_minute: number;
  per_order_rate_cents: number;
  multilingual_addon_cents: number;
  multilingual_included: boolean;
  sms_ordering_addon_cents: number;
  sms_ordering_included: boolean;
  opentable_resy_addon_cents: number;
  opentable_resy_included: boolean;
  multilingual_addon_active: boolean;
  sms_ordering_addon_active: boolean;
  opentable_resy_addon_active: boolean;
  billing_start_date?: string | null;
  expiration_date?: string | null;
  plan: Plan;
  is_group?: boolean;
  offers?: Offer[];
  is_group_scoped?: boolean;
  locations?: GroupLocation[];
  checkout_mode?: string;
}

export type OfferShape = 'single' | 'multi' | 'group';

export function offerShape(offer: Offer): OfferShape {
  if (offer.is_group && offer.offers?.length) return 'multi';
  if (offer.is_group_scoped || (offer.locations?.length ?? 0) > 0)
    return 'group';
  return 'single';
}

export type OfferState = 'open' | 'paid' | 'expired' | 'canceled';

/**
 * A calendar day, not an instant. `YYYY-MM-DD` parses as UTC midnight, and
 * the admin date picker is saved that way (`…T00:00:00Z`), so either form
 * is the named day. A real timestamp keeps its time.
 */
const CALENDAR_DAY =
  /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?(?:Z|\+00:00))?$/;

export function calendarDay(
  value: string,
): { y: number; m: number; d: number } | null {
  const match = CALENDAR_DAY.exec(value.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== m - 1 ||
    probe.getUTCDate() !== d
  )
    return null;
  return { y, m, d };
}

/** The named day, in the viewer's timezone, so US offsets don't show the day before. */
function localDayStart(day: { y: number; m: number; d: number }): Date {
  return new Date(day.y, day.m - 1, day.d);
}

export function formatOfferDate(value: string): string {
  const day = calendarDay(value);
  if (!day) return formatDate(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(localDayStart(day));
}

const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

/**
 * Stripe only starts a trial when the billing date is more than 48 hours
 * out; otherwise the first month is charged at checkout. A calendar day is
 * measured from local midnight, not from UTC midnight.
 */
export function billingStartsLater(
  value: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!value) return false;
  const day = calendarDay(value);
  const start = day ? localDayStart(day) : new Date(value);
  return (
    !Number.isNaN(start.getTime()) &&
    start.getTime() - now.getTime() > FORTY_EIGHT_HOURS
  );
}

/** True once the expiration day (local) is over, or a timestamp has passed. */
export function offerExpired(
  expiration: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!expiration) return false;
  const day = calendarDay(expiration);
  if (day)
    return now.getTime() >= localDayStart({ ...day, d: day.d + 1 }).getTime();
  const at = new Date(expiration).getTime();
  return !Number.isNaN(at) && at < now.getTime();
}

export function offerState(offer: Offer, now: Date = new Date()): OfferState {
  if (offer.status === 'paid' || offer.status === 'subscription_started')
    return 'paid';
  if (offer.status === 'canceled') return 'canceled';
  if (offer.status === 'expired' || offerExpired(offer.expiration_date, now))
    return 'expired';
  return 'open';
}

export function pricingFromOffer(o: Offer): PlanPricing {
  return {
    currency: o.currency,
    baseCents: o.base_price_cents,
    includedMinutes: o.included_minutes,
    overageCentsPerMinute: o.overage_rate_cents_per_minute,
    setupFeeCents: o.setup_fee_cents,
    perOrderCents: o.per_order_rate_cents,
    addons: {
      multilingual: {
        cents: o.multilingual_addon_cents,
        included: o.multilingual_included,
      },
      sms_ordering: {
        cents: o.sms_ordering_addon_cents,
        included: o.sms_ordering_included,
      },
      opentable_resy: {
        cents: o.opentable_resy_addon_cents,
        included: o.opentable_resy_included,
      },
    },
  };
}

export function offerAddons(o: Offer): AddonSelection {
  return {
    multilingual: o.multilingual_included || !!o.multilingual_addon_active,
    sms_ordering: o.sms_ordering_included || !!o.sms_ordering_addon_active,
    opentable_resy:
      o.opentable_resy_included || !!o.opentable_resy_addon_active,
  };
}

/** An offer becomes a chooser row; its `code` is the token to accept. */
export function optionFromOffer(o: Offer): PlanOption {
  return {
    code: o.token,
    name: o.plan?.name ?? 'Your plan',
    description: o.plan?.description,
    pricing: pricingFromOffer(o),
    hue: planHue(o.plan?.code),
    tag: { label: 'Your pricing', tone: 'accent' },
    features: o.plan?.feature_points,
  };
}

export function optionFromPlan(p: Plan): PlanOption {
  return {
    code: p.code,
    name: p.name,
    description: p.description,
    pricing: pricingFromPlan(p),
    features: p.feature_points,
    tag: p.trial_period_days
      ? { label: `${p.trial_period_days}-day trial`, tone: 'neutral' }
      : undefined,
  };
}

export const LOCATION_ADDON_FIELD: Record<
  AddonKey,
  | 'multilingual_addon_active'
  | 'sms_ordering_addon_active'
  | 'opentable_resy_addon_active'
> = {
  multilingual: 'multilingual_addon_active',
  sms_ordering: 'sms_ordering_addon_active',
  opentable_resy: 'opentable_resy_addon_active',
};

/** The offer's roster with included add-ons forced on (the server does the same). */
export function normalizedLocations(o: Offer): GroupLocation[] {
  return (o.locations ?? []).map((l) => {
    const next = { ...l };
    for (const k of ADDON_KEYS) {
      const field = LOCATION_ADDON_FIELD[k];
      next[field] = pricingFromOffer(o).addons[k].included || !!l[field];
    }
    return next;
  });
}

/** Per-location add-on charges for a group-scoped offer. */
export function locationAddonCents(
  pricing: PlanPricing,
  loc: GroupLocation,
): number {
  return ADDON_KEYS.reduce((sum, k) => {
    const a = pricing.addons[k];
    return sum + (loc[LOCATION_ADDON_FIELD[k]] && !a.included ? a.cents : 0);
  }, 0);
}

// Form -----------------------------------------------------------------------

export const LOCATION_COUNTS: { value: string; label: string }[] = [
  { value: '1', label: '1 location' },
  { value: '2-5', label: '2–5 locations' },
  { value: '6-10', label: '6–10 locations' },
  { value: '10+', label: '10+ locations' },
];

export interface BusinessForm {
  business_name: string;
  store_name: string;
  signer_name: string;
  signer_title: string;
  signer_email: string;
  signer_phone: string;
  num_locations: string;
}

export const EMPTY_FORM: BusinessForm = {
  business_name: '',
  store_name: '',
  signer_name: '',
  signer_title: '',
  signer_email: '',
  signer_phone: '',
  num_locations: '1',
};

export type FormErrors = Partial<
  Record<keyof BusinessForm | 'agree' | 'plan', string>
>;

export function validate(
  form: BusinessForm,
  agreed: boolean,
  hasPlan: boolean,
  needsStoreName: boolean,
): FormErrors {
  const e: FormErrors = {};
  if (!hasPlan) e.plan = 'Choose a plan.';
  if (!form.business_name.trim())
    e.business_name =
      'Enter the legal business name that will appear on invoices.';
  if (needsStoreName && !form.store_name.trim())
    e.store_name = 'Enter the name callers know you by.';
  if (!form.signer_name.trim())
    e.signer_name = 'Enter the name of the person signing.';
  if (
    !form.signer_email.trim() ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.signer_email.trim())
  )
    e.signer_email = 'Enter a valid email; the receipt and agreement go here.';
  if (form.signer_phone.replace(/\D/g, '').length < 10)
    e.signer_phone = 'Enter a phone number with area code.';
  if (!agreed) e.agree = 'Accept the agreement to continue.';
  return e;
}

/** Terms version accepted with every activation (`tc_version`). */
export const TC_VERSION = 'v3.0';

/** Summary of the Master Service Agreement shown above the checkbox. */
export const TERMS_SUMMARY: string[] = [
  'This Master Service Agreement (the "Agreement") governs your use of the AskArthur AI-powered host, answering, ordering, and reservation services. By completing checkout, you authorize AskArthur to deploy the AI agent on your phone lines and integrations.',
  'Key terms: Six-month initial term, followed by month-to-month auto-renewal. The one-time setup fee is charged immediately and is non-refundable. Monthly base subscription fees are billed monthly starting at the end of your trial or specified billing start date. Usage beyond your included minutes will be billed at the designated overage rate. Prepaid orders via Arthur Pay are subject to payment processing fees.',
];
